import { useState, useMemo } from "react";
import { Link, useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Plus, Loader2 } from "lucide-react";
import { AppContainer } from "@/components/layout/app-container";
import { TabsUnderline } from "@/components/ui/tabs-underline";
import { ProjectOverviewContent } from "@/components/projetos/project-overview-content";
import { ProjectDetailsContent } from "@/components/projetos/project-details-content";
import { ProjectBudgetContent } from "@/components/projetos/project-budget-content";
import { ProjectContractsContent } from "@/components/projetos/project-contracts-content";
import { ProjectPeopleContent } from "@/components/projetos/project-people-content";
import { ProjectProfileCard } from "@/components/projetos/project-profile-card";
import { ProjectLinksCard } from "@/components/projetos/project-links-card";
import { ProjectInterestsCard } from "@/components/projetos/project-interests-card";
import { ProjectTechnicalSheetCard } from "@/components/projetos/project-technical-sheet-card";
import { ProjectEvaluatedPlayersCard } from "@/components/projetos/project-evaluated-players-card";
import { InvestmentStatsCard } from "@/components/projetos/investment-stats-card";
import { AccessBudgetsCard } from "@/components/projetos/access-budgets-card";
import { IProjectPerson } from "@/components/projetos/people-table";
import { useGetProjectById } from "@/services/queries/useProject";
import {
  formatDate,
  formatMovieDuration,
  formatCurrency,
  aggregateBudgetValues,
  transformStaffContractsToPeople,
  getProducerCNPJ,
  getProducerCompanyName,
} from "@/lib/transform-movie";

// Comprehensive mock data matching Figma "Mal Estar" project
const mockProject = {
  id: 1,
  name: "Mal Estar",
  salicNumber: "1234567899",
  proponent: "Attini Investimentos",
  cnpj: "49.530.502/0001-13",
  logline: "Martha, portadora de Alzheimer, em meio ao tratamento com a enfermeira Thamar, relembra-se das atrocidades que vivenciou no Hospital Colônia de Barbacena, no ano de 1968. Confrontando sua identidade fragmentada, percebe que Thamar é uma projeção de um passado desafiador",
  coverImage: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1200&h=300&fit=crop",
  sinopse: `Em 1968, no sombrio Instituto Pavlov, Thamar – uma jovem estudante de medicina oriunda de uma infância assombrada por abusos e manipulações na isolada Fazenda Rio das Pedras – recebe a incumbência de cuidar de Martha, uma idosa acometida por Alzheimer. Conforme a paciente revela, por entre fragmentos de memória, os horrores de um passado brutal marcado pelas atrocidades do Hospital Colônia de Barbacena, Thamar inicia uma jornada de autodescoberta que a confronta com os segredos enterrados de sua própria história familiar. Martha, vive no ano de 2001 e vê Thamar como um reflexo da sua personalidade, um duplo, enquanto a trama se desenvolve relembrando a narrativa de sua vida desde a infância no interior de Goiás.

Criada na fazenda, sob a sombra implacável de sua mãe, Célia, tem a morte de seu pai aos 9 anos de idade. Na idade adulta, cuida de sua mãe até conhecer Samael– cuja influência, a despeito de sua crueldade, é indissociável da necessidade de segurança e pertencimento – Thamar é compelida a abandonar as raízes do interior e investir na medicina, profissão que um dia pertencia a seu pai. No ambiente inóspito da faculdade e do hospital, ela se vê enredada em mentiras arquitetadas por Hermes, o chefe da ala de psiquiatria, que busca ocultar os abusos da instituição.`,
  minutagem: "01:50:00",
  episodeCount: "-",
  episodeDuration: "-",
  productionPlan: `A Attini Investimentos em parceria com a D. Doors Content realizaram o desenvolvimento completo da série, com roteiro, análise técnica e tech scout das locações. O projeto já passou por produtoras e consultas por players como Urca Filmes, Camisa Listrada, Downtown filmes, Transforma e Moon Shot, alguns players demonstraram interesse na coprodução do projeto e a Downtown filmes tem interesse na distribuição sendo uma das principais parceiras do projeto. Longa foi escrito e registrado em 2020 para pós de roteiro da Fundação Armando Alvares Penteado, com nota de 9.5 final (veja o link do teaser) e será filmado em quatro estados brasileiros (Goiás, São Paulo, Belo Horizonte e Rio Grande do Norte) e em uma cena na Bolívia, com 97 cenários distribuídos entre 7 locações em São Paulo, 6 em Pirenópolis, 5 em Barbacena, 1 no Rio Grande do Norte e 1 na Bolívia. Cerca de 70% das cenas ocorrem em interiores e 30% em exteriores, com 38 cenários em São Paulo. O plano total de filmagem é de aproximadamente 50 dias, com diárias específicas para as locações internacionais (1 dia na Bolívia e 1 no Rio Grande do Norte, com equipe reduzida). A decupagem, script breakdown, ordens do dia, análise técnica e a Bíblia de Produção já estão finalizados, e o projeto está sendo enviado para editais de fundos de desenvolvimento.`,
  episodeValueUSD: undefined,
  totalValueUSD: 1500000,
  capturedValueUSD: 0,
  toCaptureValueUSD: 1500000,
  hasFinancing: false,
  financingModel: "-",
  additionalInfo: `O longa faz parte do plano de financiamento da Attini Investimentos, a primeira empresa do brasil com uma solução para investidores de Art 1A com tecnologia blockchain, apresentando protótipo em palestras no Rio2c e no RIW. A Attini irá captar para o projeto no primeiro Fórum de Investimento em Cultura que acontecera para o lançamento do sistema e para captação de recursos de projetos, que ocorrerá em junho na Fundação Armando Alvares Penteado. Orçamento mínimo para realizar o filme está cotado em R$ 8 milhões e pode ser estendido até R$ 10 milhões de acordo com a avaliação dos produtores do orçamento completo em pequenos itens. Estamos enviando a proposta de Co-produção para análise dos players. Apesar do número de locações, a maior parte do filme é gravado em internas, com facilidade de produção. A intenção deste envio para rodadas de negócios é de avaliar o desenvolvimento para início de coprodução e inscrição na lei do audiovisual.

A decupagem toma preferência pela produção com uma câmera e planos longos em sequência. O production design conta com ambientes em hospitais e fazendas.`,

  // Profile card data
  profileName: "Ricardo Portas",
  profileRole: "Perfil Proponente",
  profileAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
  propertyId: "P102025",
  constructionYear: 2025,
  projectType: "Audiovisual",
  registrationDate: "07/07/2025",

  // Links card data
  externalLink: "https://example.com/mal-estar-press-kit",
  videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
  videoThumbnail: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=400&h=200&fit=crop",

  // Interests card data
  statusProject: ["Desenvolvimento"],
  seekingTypes: ["Aquisição/ Licenciamento", "Coprodução"],
  exhibitionWindows: ["Cinema"],
  formats: ["Longa-metragem"],
  genres: ["Drama", "Terror"],
  subGenres: ["Distopia", "Ficção Científica", "Psicológica"],
  targetAudience: ["Adulto", "Pré-Escolar"],

  // Technical sheet data
  director: "Nome do Diretor",
  technicalCoverImage: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400&h=200&fit=crop",

  // Evaluated players data
  evaluatedPlayers: [
    {
      name: "Globo Filmes I Ficção",
      logoUrl: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&fit=crop",
      link: "https://my.rio2c.com/pt-br/2025/Companies/Details/11d7a366-476e-48de-b282-70add315382f",
      attendanceType: "Presencial",
    },
    {
      name: "Anonymous Content Brazil",
      logoUrl: "https://images.unsplash.com/photo-1614680376573-df3480f00c5a?w=100&h=100&fit=crop",
      attendanceType: "Presencial",
    },
    {
      name: "dlktsn",
      logoUrl: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=100&h=100&fit=crop",
      attendanceType: "Presencial",
    },
  ],

  // Budget data (Orçamentos)
  budgetMechanisms: [
    {
      name: "ART. 1º - Lei 8.685/93",
      approvedValue: 3000000.00,
      capturedValue: 2714000.00,
      balance: 286000.00,
    },
    {
      name: "ART. 1º A - Lei 8.685/93",
      approvedValue: 2360000.00,
      capturedValue: 360000.00,
      balance: 2000000.00,
    },
    {
      name: "ART. 3º - Lei 8.685/93",
      approvedValue: 3000000.00,
      capturedValue: 2809654.01,
      balance: 190345.99,
    },
    {
      name: "ART. 25 - Lei 8.313/91",
      approvedValue: 640000.00,
      capturedValue: 640000.00,
      balance: 0.00,
    },
    {
      name: "Recursos próprios/contrapartida",
      approvedValue: 1546437.50,
      capturedValue: 0.00,
      balance: 0.00,
    },
  ],
  investmentValue: 12368.00,
  investmentPercentageChange: -9,
};

// Mock people data for the project
const mockPeople: IProjectPerson[] = [
  {
    id: 1,
    name: "Ricardo Portas",
    avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
    phone: "+55 33 33333-3333",
    email: "ricardo@ddors.com",
    role: "Produtora",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 2,
    name: "Filemom Paulo",
    avatarUrl: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop",
    phone: "+55 31 99308-6295",
    email: "filemom@prumodesign.com",
    role: "UI/UX Designer",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 3,
    name: "Ricardo Portas",
    avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
    phone: "+55 33 33333-3333",
    email: "ricardo@ddors.com",
    role: "Produtora",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 4,
    name: "Filemom Paulo",
    avatarUrl: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop",
    phone: "+55 31 99308-6295",
    email: "filemom@prumodesign.com",
    role: "UI/UX Designer",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 5,
    name: "Ricardo Portas",
    avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
    phone: "+55 33 33333-3333",
    email: "ricardo@ddors.com",
    role: "Produtora",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 6,
    name: "Filemom Paulo",
    avatarUrl: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop",
    phone: "+55 31 99308-6295",
    email: "filemom@prumodesign.com",
    role: "UI/UX Designer",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 7,
    name: "Ricardo Portas",
    avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
    phone: "+55 33 33333-3333",
    email: "ricardo@ddors.com",
    role: "Produtora",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 8,
    name: "Filemom Paulo",
    avatarUrl: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop",
    phone: "+55 31 99308-6295",
    email: "filemom@prumodesign.com",
    role: "UI/UX Designer",
    status: "Ativo",
    registrationDate: "10/06/2025",
  },
  {
    id: 9,
    name: "Maria Silva",
    avatarUrl: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
    phone: "+55 11 98765-4321",
    email: "maria.silva@email.com",
    role: "Diretora",
    status: "Ativo",
    registrationDate: "15/05/2025",
  },
  {
    id: 10,
    name: "João Santos",
    avatarUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop",
    phone: "+55 21 99876-5432",
    email: "joao.santos@email.com",
    role: "Roteirista",
    status: "Ativo",
    registrationDate: "20/05/2025",
  },
  {
    id: 11,
    name: "Ana Costa",
    phone: "+55 85 98888-7777",
    email: "ana.costa@email.com",
    role: "Fotógrafa",
    status: "Ativo",
    registrationDate: "25/05/2025",
  },
  {
    id: 12,
    name: "Carlos Oliveira",
    phone: "+55 71 97777-6666",
    email: "carlos.oliveira@email.com",
    role: "Editor",
    status: "Inativo",
    registrationDate: "01/06/2025",
  },
];

const tabs = ["Visão Geral", "Detalhes", "Orçamentos", "Contratos", "Pessoas"];

export default function ProjetoDetails() {
  const params = useParams();
  const projectId = Number(params.id);
  const [activeTab, setActiveTab] = useState<string>("Visão Geral");

  // Fetch project from API
  const { data: apiProject, isLoading, error } = useGetProjectById(projectId);

  // Transform API data to component format
  const project = useMemo(() => {
    if (!apiProject) return null;

    // Aggregate budget values
    const budgetAgg = aggregateBudgetValues(apiProject.budgets);

    return {
      id: apiProject.id,
      name: apiProject.name,
      salicNumber: apiProject.salic_number,
      proponent: getProducerCompanyName(apiProject.producer),
      cnpj: getProducerCNPJ(apiProject.producer),
      logline: apiProject.logline || '',
      coverImage: apiProject.logo_url || '',
      sinopse: apiProject.description || '',
      minutagem: formatMovieDuration(apiProject.movie_time),
      episodeCount: apiProject.number_of_episodes?.toString() || '-',
      episodeDuration: apiProject.time_per_episode?.toString() || '-',
      productionPlan: apiProject.pos_production_description || '',
      episodeValueUSD: undefined,
      totalValueUSD: budgetAgg.totalApproved,
      capturedValueUSD: budgetAgg.totalCaptured,
      toCaptureValueUSD: budgetAgg.totalRemaining,
      hasFinancing: (apiProject.budgets?.length ?? 0) > 0,
      financingModel: apiProject.budgets?.[0]?.budgetStep?.description || '-',
      additionalInfo: apiProject.media_kit_url || '',

      // Profile card
      profileName: apiProject.producer?.name || 'Não informado',
      profileRole: 'Perfil Proponente',
      profileAvatar: apiProject.producer?.profile_picture || '',
      propertyId: `P${apiProject.id}`,
      constructionYear: apiProject.release_date ? new Date(apiProject.release_date).getFullYear() : new Date().getFullYear(),
      projectType: apiProject.movie_format || 'Audiovisual',
      registrationDate: formatDate(apiProject.release_date),

      // Links card
      externalLink: apiProject.media_kit_url || '',
      videoUrl: apiProject.movie_teaser_url || '',
      videoThumbnail: apiProject.logo_url || '',

      // Interests card
      statusProject: apiProject.status?.description ? [apiProject.status.description] : [],
      seekingTypes: [], // Not in backend model
      exhibitionWindows: [], // Not in backend model
      formats: apiProject.movie_format ? [apiProject.movie_format] : [],
      genres: apiProject.genres?.map(g => g.genre.description) || [],
      subGenres: apiProject.genres?.filter(g => g.sub_genre).map(g => g.genre.description) || [],
      targetAudience: apiProject.publicTypes?.map((pt: any) => pt.publicType?.description).filter(Boolean) || [],

      // Technical sheet
      director: 'Nome do Diretor', // Not in backend model
      technicalCoverImage: apiProject.logo_url || '',

      // Evaluated players (not in backend)
      evaluatedPlayers: [],

      // Budget mechanisms
      budgetMechanisms: apiProject?.budgets?.map(b => ({
        name: b.budgetStep?.description || 'Recurso',
        approvedValue: parseFloat(b.total_amount || '0'),
        capturedValue: parseFloat(b.amount || '0'),
        balance: parseFloat(b.total_amount || '0') - parseFloat(b.amount || '0'),
      })) || [],

      // Investment stats
      investmentValue: budgetAgg.totalCaptured,
      investmentPercentageChange: 0, // Calculate from historical data if available
    };
  }, [apiProject]);

  // Transform staff contracts to people
  const people = useMemo(() => {
    if (!apiProject?.staffContracts) return [];
    return transformStaffContractsToPeople(apiProject.staffContracts);
  }, [apiProject]);

  // Loading state
  if (isLoading) {
    return (
      <AppContainer>
        <div className="flex justify-center items-center py-24">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
          <span className="ml-3 text-gray-400 text-lg">Carregando projeto...</span>
        </div>
      </AppContainer>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <AppContainer>
        <div className="bg-red-500/10 border border-red-500 rounded-xl p-8">
          <h2 className="text-xl font-bold text-red-500 mb-2">
            Erro ao carregar projeto
          </h2>
          <p className="text-red-400">
            Não foi possível carregar os detalhes do projeto. Por favor, tente novamente.
          </p>
          <Link href="/projetos">
            <Button variant="outline" className="mt-4">
              Voltar para Projetos
            </Button>
          </Link>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <div className="flex flex-col gap-[29px]">
        {/* Page Header */}
        <div className="flex items-center gap-[28px]">
          {/* Purple vertical bar */}
          <div className="bg-[#7846FF] w-[3px] h-full rounded-[10px] self-stretch" />

          {/* Title section */}
          <div className="flex flex-col items-start leading-normal flex-1">
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-white text-[24px]">
              Projetos
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[#d1d1d1] text-[14px] whitespace-pre">
              Texto de Apoio da página
            </p>
          </div>

          {/* Action buttons */}
          <Link href={`/cadastrar-projeto?id=${projectId}`}>
            <Button
              variant="outline"
              className="h-[50px] w-[173px] gap-[3px] border-[#7846FF]"
            >
              <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f5f2ff] text-[18px] whitespace-pre">
                Editar Projeto
              </p>
            </Button>
          </Link>

          <Link href="/cadastrar-projeto">
            <Button className="bg-[#7846FF] h-[50px] w-[173px] gap-[3px]">
              <Plus className="w-[24px] h-[24px]" />
              <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f5f2ff] text-[18px] whitespace-pre">
                Novo Projeto
              </p>
            </Button>
          </Link>
        </div>

        {/* Main Content Card */}
        <div className="bg-[#15203C] rounded-[20px] p-[30px] flex flex-col gap-[30px]">
          {/* Tabs Navigation */}
          <div className="border-b border-gray-700">
            <TabsUnderline
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Tab Content */}
          {activeTab === "Visão Geral" && (
            <div className="flex gap-[40px]">
              {/* Left Content */}
              <div className="w-[1043px]">
                <ProjectOverviewContent
                  name={project.name}
                  salicNumber={project.salicNumber}
                  proponent={project.proponent}
                  cnpj={project.cnpj}
                  logline={project.logline}
                  coverImage={project.coverImage}
                  sinopse={project.sinopse}
                  minutagem={project.minutagem}
                  episodeCount={project.episodeCount}
                  episodeDuration={project.episodeDuration}
                  productionPlan={project.productionPlan}
                  episodeValueUSD={project.episodeValueUSD}
                  totalValueUSD={project.totalValueUSD}
                  capturedValueUSD={project.capturedValueUSD}
                  toCaptureValueUSD={project.toCaptureValueUSD}
                  hasFinancing={project.hasFinancing}
                  financingModel={project.financingModel}
                  additionalInfo={project.additionalInfo}
                />
              </div>

              {/* Right Sidebar */}
              <div className="w-[386px] flex flex-col gap-[30px]">
                <ProjectProfileCard
                  name={project.profileName}
                  role={project.profileRole}
                  avatarUrl={project.profileAvatar}
                  propertyId={project.propertyId}
                  constructionYear={project.constructionYear}
                  projectType={project.projectType}
                  registrationDate={project.registrationDate}
                />

                <ProjectLinksCard
                  externalLink={project.externalLink}
                  videoUrl={project.videoUrl}
                  videoThumbnail={project.videoThumbnail}
                />

                <ProjectInterestsCard
                  statusProject={project.statusProject}
                  seekingTypes={project.seekingTypes}
                  exhibitionWindows={project.exhibitionWindows}
                  formats={project.formats}
                  genres={project.genres}
                  subGenres={project.subGenres}
                  targetAudience={project.targetAudience}
                />
              </div>
            </div>
          )}

          {activeTab === "Detalhes" && (
            <div className="flex gap-[40px]">
              {/* Left Content */}
              <div className="w-[1043px]">
                <ProjectDetailsContent
                  name={project.name}
                  salicNumber={project.salicNumber}
                  proponent={project.proponent}
                  cnpj={project.cnpj}
                  logline={project.logline}
                  productionPlan={project.productionPlan}
                />
              </div>

              {/* Right Sidebar */}
              <div className="w-[386px] flex flex-col gap-[30px]">
                <ProjectTechnicalSheetCard
                  coverImage={project.technicalCoverImage}
                  projectTitle={project.name}
                  director={project.director}
                  projectType={project.projectType}
                />

                <ProjectEvaluatedPlayersCard
                  players={project.evaluatedPlayers}
                />
              </div>
            </div>
          )}

          {activeTab === "Orçamentos" && (
            <div className="flex gap-[40px]">
              {/* Left Content */}
              <div className="w-[1023px]">
                <ProjectBudgetContent
                  name={project.name}
                  salicNumber={project.salicNumber}
                  proponent={project.proponent}
                  cnpj={project.cnpj}
                  logline={project.logline}
                  budgetMechanisms={project.budgetMechanisms}
                />
              </div>

              {/* Right Sidebar */}
              <div className="w-[403px] flex flex-col gap-[30px]">
                <InvestmentStatsCard
                  investmentValue={project.investmentValue}
                  percentageChange={project.investmentPercentageChange}
                />

                <AccessBudgetsCard />
              </div>
            </div>
          )}

          {activeTab === "Contratos" && (
            <ProjectContractsContent
              movieId={Number(projectId) || project.id}
              name={project.name}
              salicNumber={project.salicNumber}
              proponent={project.proponent}
              cnpj={project.cnpj}
            />
          )}

          {activeTab === "Pessoas" && (
            <ProjectPeopleContent
              name={project.name}
              salicNumber={project.salicNumber}
              proponent={project.proponent}
              cnpj={project.cnpj}
              people={people}
              onView={(id) => console.log("View person:", id)}
              onEdit={(id) => console.log("Edit person:", id)}
              onDelete={(id) => console.log("Delete person:", id)}
            />
          )}
        </div>
      </div>
    </AppContainer>
  );
}
